import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Button,
  Alert
} from '@mui/material';
import { useAppNavigation } from '../hooks/useAppNavigation';
import Layout from '../components/Layout';
import LoadingOverlay from '../components/LoadingOverlay';
import { placeholderImage, getBestItemImageUrl } from '../utils/imageUtils';
import boxService from '../services/boxService';
import offlineApi from '../services/offlineApi';
import BoxThumbnail from '../components/BoxThumbnail';

// Using the shared BoxThumbnail component instead of a local implementation
// This ensures consistent behavior across the application

const OfflineItems = () => {
  const navigate = useAppNavigation();
  const [boxes, setBoxes] = useState([]);
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load data from offline API
  const loadOfflineData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // First make sure we have test data
      await boxService.checkOfflineData();
      
      // Get boxes and items from offline API
      const boxesData = await offlineApi.getAllBoxes();
      const itemsData = await offlineApi.getAllItems();
      
      console.log('OfflineItems: Retrieved data from offline API:', {
        boxes: boxesData?.length || 0,
        items: itemsData?.length || 0,
        firstBox: boxesData?.[0] || null
      });
      
      if (!boxesData || boxesData.length === 0) {
        setError('No boxes found in offline storage');
        setBoxes([]);
        setItems([]);
        return;
      }
      
      // Properly associate items with boxes
      const boxesWithItems = boxesData.map(box => {
        // Use loose equality (==) for ID comparison to handle string/number type differences
        const boxItems = itemsData.filter(item => 
          item.box_id === box.id || item.album_id === box.id
        );
        
        return {
          ...box,
          items: boxItems,
          items_count: boxItems.length,
          item_count: boxItems.length
        };
      });
      
      // Update state
      setBoxes(boxesWithItems);
      setItems(itemsData);
      console.log('OfflineItems: Boxes with items:', boxesWithItems);
      
    } catch (error) {
      console.error('Error loading offline data:', error);
      setError('Failed to load offline data: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Navigate to box
  const handleBoxClick = (boxId) => {
    navigate.to.BOX_DETAIL(boxId);
  };

  // Navigate to item
  const handleItemClick = (itemId) => {
    navigate.to.ITEM_EDIT(itemId);
  };

  // Return to the main page
  const handleBack = () => {
    navigate.to.HOME();
  };

  return (
    <Layout title="Offline Items" onBack={handleBack}>
      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Offline Mode
        </Typography>
        <Typography variant="body2" gutterBottom>
          You are currently offline. Click the button below to load data from your device.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          sx={{ mt: 2 }}
          onClick={loadOfflineData}
        >
          Load Offline Data
        </Button>
      </Paper>
      
      {isLoading ? (
        <LoadingOverlay />
      ) : error ? (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      ) : (
        <>
          {/* Debug info */}
          {boxes.length > 0 && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2">Debug Info:</Typography>
              <Box component="pre" sx={{ fontSize: '12px', mt: 1, maxHeight: '200px', overflow: 'auto' }}>
                {JSON.stringify({
                  boxCount: boxes.length,
                  itemCount: items.length,
                  firstBox: { id: boxes[0].id, name: boxes[0].name, itemCount: boxes[0].items.length }
                }, null, 2)}
              </Box>
            </Paper>
          )}
          
          {/* Boxes Section */}
          {boxes.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
                Boxes ({boxes.length}):
              </Typography>
              
              <Grid container spacing={2}>
                {boxes.map((box) => (
                  <Grid item xs={6} sm={4} md={3} key={box.id}>
                    <Card sx={{ height: '100%' }}>
                      <CardActionArea 
                        onClick={() => handleBoxClick(box.id)}
                        sx={{ height: '100%', p: 1 }}
                      >
                        <BoxThumbnail boxId={box.id} height={120} />
                        <CardContent>
                          <Typography gutterBottom variant="h6" component="div" noWrap>
                            {box.name}
                          </Typography>
                          
                          <Typography variant="body2" color="text.secondary">
                            {box.items_count || 0} {box.items_count === 1 ? 'item' : 'items'}
                          </Typography>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
          
          {/* Items Section */}
          {items.length > 0 && (
            <Box>
              <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
                Items ({items.length}):
              </Typography>
              
              <Grid container spacing={1}>
                {items.map((item) => (
                  <Grid item xs={4} sm={3} md={2} key={item.id}>
                    <Card sx={{ height: '100%' }}>
                      <CardActionArea 
                        onClick={() => handleItemClick(item.id)}
                        sx={{ height: '100%' }}
                      >
                        <Box 
                          sx={{
                            height: 0,
                            paddingTop: '100%', // 1:1 aspect ratio
                            position: 'relative',
                            backgroundColor: 'rgba(0,0,0,0.04)'
                          }}
                        >
                          <Box
                            component="img"
                            src={getBestItemImageUrl(item) || placeholderImage}
                            alt={item.name || 'Item'}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = placeholderImage;
                            }}
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                        </Box>
                      </CardActionArea>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
          
          {boxes.length === 0 && items.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1">
                No offline data found. Click the button above to load data.
              </Typography>
            </Box>
          )}
        </>
      )}
    </Layout>
  );
};

export default OfflineItems;
